import { useEffect, useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { useHealth } from '../hooks/useApi';

gsap.registerPlugin(ScrollTrigger);

export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const coreRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const subheadlineRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const microcopyLeftRef = useRef<HTMLDivElement>(null);
  const microcopyRightRef = useRef<HTMLDivElement>(null);
  const scrollHintRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  
  const { data: health } = useHealth();

  // Load animation on mount
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      // Initial states
      gsap.set(coreRef.current, { scale: 0.65, opacity: 0 });
      gsap.set(labelRef.current, { y: 10, opacity: 0 });
      gsap.set(headlineRef.current, { opacity: 0 });
      gsap.set(subheadlineRef.current, { y: 14, opacity: 0 });
      gsap.set(ctaRef.current, { y: 14, opacity: 0 });
      gsap.set([microcopyLeftRef.current, microcopyRightRef.current], { opacity: 0 });
      gsap.set(scrollHintRef.current, { opacity: 0 });

      // Animation sequence
      tl.to(coreRef.current, { scale: 1, opacity: 1, duration: 0.9 }, 0)
        .to(labelRef.current, { y: 0, opacity: 1, duration: 0.5 }, 0.25)
        .to(headlineRef.current, { opacity: 1, duration: 0.8 }, 0.3)
        .to(subheadlineRef.current, { y: 0, opacity: 1, duration: 0.6 }, 0.45)
        .to(ctaRef.current, { y: 0, opacity: 1, duration: 0.6 }, 0.55)
        .to([microcopyLeftRef.current, microcopyRightRef.current], { opacity: 1, duration: 0.5 }, 0.7)
        .to(scrollHintRef.current, { opacity: 1, duration: 0.5 }, 0.8);
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Scroll-driven exit animation
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=130%',
          pin: true,
          scrub: 0.6,
          onLeaveBack: () => {
            gsap.to(coreRef.current, { scale: 1, opacity: 1, duration: 0.3 });
            gsap.to(headlineRef.current, { scale: 1, opacity: 1, duration: 0.3 });
            gsap.to(subheadlineRef.current, { y: 0, opacity: 1, duration: 0.3 });
            gsap.to(ctaRef.current, { y: 0, opacity: 1, duration: 0.3 });
            gsap.to([microcopyLeftRef.current, microcopyRightRef.current], { opacity: 1, duration: 0.3 });
            gsap.to(scrollHintRef.current, { opacity: 1, duration: 0.3 });
            gsap.to(overlayRef.current, { opacity: 0, duration: 0.3 });
          },
        },
      });

      // EXIT (70-100%)
      scrollTl
        .fromTo(
          coreRef.current,
          { scale: 1, opacity: 1 },
          { scale: 0.55, opacity: 0.25, ease: 'power2.in' },
          0.7
        )
        .fromTo(
          headlineRef.current,
          { scale: 1, opacity: 1 },
          { scale: 0.9, opacity: 0.2, ease: 'power2.in' },
          0.7
        )
        .fromTo(
          subheadlineRef.current,
          { y: 0, opacity: 1 },
          { y: -20, opacity: 0, ease: 'power2.in' },
          0.75
        )
        .fromTo(
          ctaRef.current,
          { y: 0, opacity: 1 },
          { y: -10, opacity: 0, ease: 'power2.in' },
          0.78
        )
        .fromTo(
          [microcopyLeftRef.current, microcopyRightRef.current],
          { opacity: 1 },
          { opacity: 0, ease: 'power2.in' },
          0.8
        )
        .fromTo(
          scrollHintRef.current,
          { opacity: 1 },
          { opacity: 0, ease: 'power2.in' },
          0.82
        )
        .fromTo(
          overlayRef.current,
          { opacity: 0 },
          { opacity: 1, ease: 'none' },
          0.82
        );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const scrollToNext = () => {
    const nextSection = document.querySelector('#session-field');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      ref={sectionRef}
      className="section-pinned bg-synapse-black flex items-center justify-center overflow-hidden"
    >
      {/* Radial gradient background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 45%, rgba(243,195,86,0.10), rgba(7,8,10,1) 55%)',
        }}
      />

      {/* Black transition overlay */}
      <div
        ref={overlayRef}
        className="absolute inset-0 bg-synapse-black pointer-events-none z-50"
        style={{ opacity: 0 }}
      />

      {/* Core container */}
      <div className="relative z-10 flex flex-col items-center justify-center">
        {/* Core Ring */}
        <div
          ref={coreRef}
          className="relative w-[min(34vw,420px)] h-[min(34vw,420px)] rounded-full animate-core-breathe will-change-transform"
          style={{
            background: 'rgba(243,195,86,0.08)',
            border: '2px solid rgba(243,195,86,0.65)',
            boxShadow: '0 0 60px rgba(243,195,86,0.15), inset 0 0 60px rgba(243,195,86,0.05)',
          }}
        >
          {/* Inner content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {/* System Label */}
            <div
              ref={labelRef}
              className="font-mono text-[10px] lg:text-xs tracking-[0.2em] text-synapse-gold/80 mb-4"
            >
              {health?.status === 'healthy' ? '(attentive)' : '(settling)'}
            </div>

            {/* Main Headline */}
            <h1
              ref={headlineRef}
              className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white tracking-tight text-center"
            >
              Synaptic Clone
            </h1>

            {/* Subheadline */}
            <p
              ref={subheadlineRef}
              className="mt-4 text-sm sm:text-base lg:text-lg text-synapse-gray-300 text-center max-w-md px-4"
            >
              One surface. Clear intent. Quiet confirmation.
            </p>

            {/* CTA Button */}
            <div ref={ctaRef} className="mt-8">
              <Button
                onClick={scrollToNext}
                className="bg-synapse-gold text-synapse-black hover:bg-synapse-amber font-medium px-6 py-3 rounded-xl btn-hover"
              >
                Begin
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom microcopy - left */}
      <div
        ref={microcopyLeftRef}
        className="absolute left-6 lg:left-12 bottom-[14vh] font-mono text-[10px] lg:text-xs tracking-wider text-white/40"
      >
        (awaiting selection)
      </div>

      {/* Bottom microcopy - right */}
      <div
        ref={microcopyRightRef}
        className="absolute right-6 lg:right-12 bottom-[14vh] font-mono text-[10px] lg:text-xs tracking-wider text-white/40 text-right"
      >
        (quiet until touched)
      </div>

      {/* Scroll hint */}
      <div
        ref={scrollHintRef}
        className="absolute bottom-[6vh] left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer"
        onClick={scrollToNext}
      >
        <span className="font-mono text-[10px] tracking-[0.15em] text-white/30">
          SCROLL TO ENTER
        </span>
        <ChevronDown className="w-4 h-4 text-white/30 animate-bounce" />
      </div>
    </section>
  );
}
